/**
 * Base-path helpers for deploying the portal under a URL sub-path.
 *
 * The portal is served from `https://<host>/patient-portal/` on the shared
 * dev/staging host, which `vite.config.ts` declares as `base:
 * '/patient-portal/'`. That setting only rewrites *asset* URLs — it tells
 * React Router and `window.location` consumers nothing. Without the helpers
 * here:
 *
 *   - React Router saw `location.pathname === '/patient-portal/'` and
 *     matched it against the single-segment `:token` catch-all, so the
 *     landing page treated the literal string "patient-portal" as a
 *     magic-link token instead of showing the login page.
 *   - `window.location.href = '/login'` navigated to the *host root*, which
 *     on a shared host is a different app entirely.
 *   - `window.location.pathname` fed into `?next=` carried the
 *     `/patient-portal` prefix, which React Router would then prepend a
 *     second time (`/patient-portal/patient-portal/pay`).
 *
 * `BASE_URL` is inlined by Vite at build time, so this costs nothing at
 * runtime and collapses to a no-op when the app is served from the root.
 */

/**
 * Vite's `base` without its trailing slash — `'/patient-portal'`, or `''`
 * when the app is served from the host root (including under Vitest, where
 * no `base` is configured).
 */
export const BASE_PATH = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');

/**
 * React Router's `basename`. Router wants `'/'` for "no prefix", whereas
 * string concatenation wants `''` — hence the two constants.
 */
export const ROUTER_BASENAME = BASE_PATH || '/';

/**
 * Turn an app-relative path (`/pay`) into a browser path
 * (`/patient-portal/pay`).
 *
 * Use for anything handed to `window.location`, or to a third party that
 * will navigate the browser back to us — Stripe's `return_url`, for one.
 */
export function withBasePath(path: string): string {
  if (!BASE_PATH) return path;
  return `${BASE_PATH}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Turn a browser path (`/patient-portal/pay`) back into an app-relative one
 * (`/pay`), which is what the route table and `?next=` deal in.
 *
 * A pathname that does not sit under the base path is returned unchanged —
 * there is nothing sensible to strip.
 */
export function stripBasePath(pathname: string): string {
  if (!BASE_PATH) return pathname;
  if (pathname === BASE_PATH) return '/';
  if (pathname.startsWith(`${BASE_PATH}/`)) {
    return pathname.slice(BASE_PATH.length) || '/';
  }
  return pathname;
}
